import React, { useState, useContext, useEffect, useCallback } from "react";
import { AuthContext } from "../context/auth-context";
import { poiUploadMusic } from "../api/poi/poi";
import { AudioAsset as PoiUploadedAudioAsset } from "../api/poi/response";
import {
  useApiAudioAssets,
  apiUpdateAudioAsset,
  apiDeleteAudioAsset,
  useApiPersonas,
} from "../api/api";
import {
  AudioAssetModel,
  AudioAssetUpdatePayload,
  PersonaBrief,
  LinkedPersonaAttributes,
  LinkedPersonaData,
  AudioAssetPersonaLinkPayload,
} from "../model/audioAsset";
import { PersonaModel } from "../model/persona";
import NavBar from "../components/layout/NavBar";
import HeaderBar from "../components/layout/Headerbar";
import musicIcon from "../assets/music.png";
import LargeButton from "../components/input/LargeButton";
import SmallButton from "../components/input/SmallButton";
import AudioPicker from "../components/input/AudioPicker";
import Modal from "../components/layout/Modal";
import "./AudioAssetsPage.css";

// Helper function to format milliseconds to MM:SS
const formatDuration = (durationMs: number): string => {
  if (isNaN(durationMs) || durationMs < 0) {
    return "00:00";
  }
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const paddedMinutes = String(minutes).padStart(2, "0");
  const paddedSeconds = String(seconds).padStart(2, "0");
  return `${paddedMinutes}:${paddedSeconds}`;
};

// Interface for the state of an editable persona link in the modal
interface EditablePersonaLink extends LinkedPersonaAttributes {
  personaId: number; // From PersonaBrief.id or LinkedPersonaData.id
  personaName?: string; // From PersonaBrief.name or LinkedPersonaData.name
  personaOrder?: number; // Added for sorting
}

const AudioAssetsPage: React.FC = () => {
  const auth = useContext(AuthContext);
  const currentUser = (auth as any)?.currentUser;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);

  const {
    data: audioAssetsData,
    loading: isLoadingAssets,
    error: errorLoadingAssets,
    refresh: fetchAudioAssets,
  } = useApiAudioAssets();
  const audioAssets: AudioAssetModel[] = audioAssetsData || [];

  // Sort audio assets: active first, then by ID descending
  const sortedAudioAssets = [...audioAssets].sort((a, b) => {
    if (a.isActive && !b.isActive) return -1;
    if (!a.isActive && b.isActive) return 1;
    return b.id - a.id; // Sort by ID descending
  });

  const {
    data: personasData,
    loading: isLoadingPersonas,
    error: errorLoadingPersonas,
  } = useApiPersonas();
  const allPersonas: PersonaBrief[] = (personasData || []).map(
    (p: PersonaModel) => ({ id: p.id, name: p.name, order: p.order })
  );

  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editingAsset, setEditingAsset] = useState<AudioAssetModel | null>(
    null
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [modalName, setModalName] = useState("");
  const [modalDescription, setModalDescription] = useState("");
  const [modalType, setModalType] = useState("");
  const [modalIsActive, setModalIsActive] = useState(true);
  const [modalDefaultVolume, setModalDefaultVolume] = useState<number | string>(
    ""
  );
  const [modalDefaultFadeInMs, setModalDefaultFadeInMs] = useState<
    number | string
  >("");
  const [modalDefaultFadeOutMs, setModalDefaultFadeOutMs] = useState<
    number | string
  >("");
  const [modalDefaultFadeCrossMs, setModalDefaultFadeCrossMs] = useState<
    number | string
  >("");
  const [modalPersonaLinks, setModalPersonaLinks] = useState<
    EditablePersonaLink[]
  >([]);

  // State for audio playback
  const [currentlyPlayingUrl, setCurrentlyPlayingUrl] = useState<string | null>(
    null
  );
  const audioPlayerRef = React.useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    // Initialize audio player
    audioPlayerRef.current = new Audio();
    const player = audioPlayerRef.current;

    const handlePlaybackEnd = () => {
      setCurrentlyPlayingUrl(null);
    };

    player.addEventListener("ended", handlePlaybackEnd);
    player.addEventListener("pause", handlePlaybackEnd); // Also reset on manual pause

    return () => {
      // Cleanup: remove event listeners and pause audio if playing
      if (player) {
        player.removeEventListener("ended", handlePlaybackEnd);
        player.removeEventListener("pause", handlePlaybackEnd);
        player.pause();
      }
    };
  }, []);

  const handlePlayAudio = (audioUrl: string | undefined) => {
    if (!audioUrl || !audioPlayerRef.current) return;
    setUploadMessage(null);
    const player = audioPlayerRef.current;
    if (currentlyPlayingUrl === audioUrl) {
      player.pause();
      setCurrentlyPlayingUrl(null);
    } else {
      if (currentlyPlayingUrl && player.src !== audioUrl) {
        player.pause(); // Stop previous audio if different
      }
      player.src = audioUrl;
      player.play().catch((error) => {
        console.error("Error playing audio:", error);
        setUploadMessage(`Error playing audio: ${error.message}`); // Show error to user
        setCurrentlyPlayingUrl(null);
      });
      setCurrentlyPlayingUrl(audioUrl);
    }
  };

  const getToken = useCallback(async (): Promise<string | null> => {
    if (currentUser) {
      return await (currentUser as any).getIdToken();
    }
    const authError = "User not authenticated.";
    setUploadMessage(authError);
    setSaveError(authError);
    return null;
  }, [currentUser]);

  useEffect(() => {}, []);

  const handleAudioSelected = (file: File) => {
    setSelectedFile(file);
    setUploadMessage(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadMessage("Please select a file first.");
      return;
    }
    const token = await getToken();
    if (!token) return;

    setIsUploading(true);
    setUploadMessage("Uploading...");
    try {
      const uploadedAssetInfo: PoiUploadedAudioAsset = await poiUploadMusic(
        selectedFile,
        token
      );
      setUploadMessage(
        `Upload successful: ${uploadedAssetInfo.name} (ID: ${uploadedAssetInfo.id})`
      );
      setSelectedFile(null);
      fetchAudioAssets();
    } catch (error: any) {
      setUploadMessage(`Upload failed: ${error.message}`);
    }
    setIsUploading(false);
  };

  const handleEdit = useCallback(
    (asset: AudioAssetModel) => {
      setEditingAsset(asset);
      setModalName(asset.name);
      setModalDescription(asset.description || "");
      setModalType(asset.type || "");
      setModalIsActive(asset.isActive);
      setModalDefaultVolume(
        asset.defaultVolume === null || asset.defaultVolume === undefined
          ? ""
          : asset.defaultVolume
      );
      setModalDefaultFadeInMs(
        asset.defaultFadeInMs === null || asset.defaultFadeInMs === undefined
          ? ""
          : asset.defaultFadeInMs
      );
      setModalDefaultFadeOutMs(
        asset.defaultFadeOutMs === null || asset.defaultFadeOutMs === undefined
          ? ""
          : asset.defaultFadeOutMs
      );
      setModalDefaultFadeCrossMs(
        asset.defaultFadeCrossMs === null ||
          asset.defaultFadeCrossMs === undefined
          ? ""
          : asset.defaultFadeCrossMs
      );

      const currentApiLinks = asset.personas || [];
      let editableLinks: EditablePersonaLink[] = allPersonas.map((p: PersonaBrief) => {
        const existingLinkData = currentApiLinks.find((l: LinkedPersonaData) => l.id === p.id);
        if (existingLinkData) {
          return {
            personaId: existingLinkData.id,
            personaName: existingLinkData.name,
            personaOrder: p.order,
            isActive: existingLinkData.isActive,
            volumeOverride: existingLinkData.volumeOverride,
            fadeInOverrideMs: existingLinkData.fadeInOverrideMs,
            fadeOutOverrideMs: existingLinkData.fadeOutOverrideMs,
            fadeCrossOverrideMs: existingLinkData.fadeCrossOverrideMs,
          };
        }
        return { 
          personaId: p.id, 
          personaName: p.name, 
          personaOrder: p.order,
          isActive: false, 
          volumeOverride: undefined,
          fadeInOverrideMs: undefined,
          fadeOutOverrideMs: undefined,
          fadeCrossOverrideMs: undefined,
        };
      });

      // Sort editableLinks: active first, then by personaOrder ascending
      editableLinks.sort((a, b) => {
        if (a.isActive && !b.isActive) return -1;
        if (!a.isActive && b.isActive) return 1;
        // If personaOrder is undefined, treat it as a large number to sort it last or handle as needed
        const orderA = a.personaOrder === undefined ? Infinity : a.personaOrder;
        const orderB = b.personaOrder === undefined ? Infinity : b.personaOrder;
        return orderA - orderB;
      });

      setModalPersonaLinks(editableLinks);

      setShowEditModal(true);
      setSaveError(null);
    },
    [allPersonas]
  );

  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingAsset(null);
  };

  const handleSaveModal = async () => {
    if (!editingAsset) return;
    setIsSaving(true);
    setSaveError(null);

    const payload: AudioAssetUpdatePayload = {
      name: modalName.trim() === "" ? editingAsset.name : modalName.trim(),
      description: modalDescription.trim(),
      type: modalType.trim(),
      isActive: modalIsActive,
      defaultVolume:
        modalDefaultVolume === ""
          ? null
          : parseFloat(modalDefaultVolume as string),
      defaultFadeInMs:
        modalDefaultFadeInMs === ""
          ? null
          : parseInt(modalDefaultFadeInMs as string, 10),
      defaultFadeOutMs:
        modalDefaultFadeOutMs === ""
          ? null
          : parseInt(modalDefaultFadeOutMs as string, 10),
      defaultFadeCrossMs:
        modalDefaultFadeCrossMs === ""
          ? null
          : parseInt(modalDefaultFadeCrossMs as string, 10),
      personaLinks: modalPersonaLinks
        .filter((link) => {
          const hasOverrides =
            link.volumeOverride !== undefined ||
            link.fadeInOverrideMs !== undefined ||
            link.fadeOutOverrideMs !== undefined ||
            link.fadeCrossOverrideMs !== undefined;
          return link.isActive || hasOverrides;
        })
        .map(
          (link: EditablePersonaLink): AudioAssetPersonaLinkPayload => ({
            personaId: link.personaId,
            isActive: link.isActive,
            volumeOverride:
              link.volumeOverride === undefined ||
              String(link.volumeOverride).trim() === ""
                ? null
                : parseFloat(String(link.volumeOverride)),
            fadeInOverrideMs:
              link.fadeInOverrideMs === undefined ||
              String(link.fadeInOverrideMs).trim() === ""
                ? null
                : parseInt(String(link.fadeInOverrideMs), 10),
            fadeOutOverrideMs:
              link.fadeOutOverrideMs === undefined ||
              String(link.fadeOutOverrideMs).trim() === ""
                ? null
                : parseInt(String(link.fadeOutOverrideMs), 10),
            fadeCrossOverrideMs:
              link.fadeCrossOverrideMs === undefined ||
              String(link.fadeCrossOverrideMs).trim() === ""
                ? null
                : parseInt(String(link.fadeCrossOverrideMs), 10),
          })
        ),
    };

    if (payload.name === "") delete payload.name;
    if (payload.description === "") payload.description = null;
    if (payload.type === "") payload.type = undefined;

    try {
      await apiUpdateAudioAsset(editingAsset.id, payload);
      fetchAudioAssets();
      handleCloseModal();
    } catch (error: any) {
      setSaveError(`Failed to save audio asset: ${error.message}`);
    }
    setIsSaving(false);
  };

  const handleDelete = async (assetId: number) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this audio asset? This action cannot be undone."
      )
    ) {
      return;
    }
    try {
      await apiDeleteAudioAsset(assetId);
      fetchAudioAssets();
    } catch (error: any) {
      alert(`Failed to delete audio asset: ${error.message}`);
    }
  };

  const handlePersonaLinkChange = (
    personaId: number,
    field: keyof EditablePersonaLink,
    value: any
  ) => {
    setModalPersonaLinks((prevLinks) =>
      prevLinks.map((link) =>
        link.personaId === personaId ? { ...link, [field]: value } : link
      )
    );
  };

  const combinedError = errorLoadingAssets || errorLoadingPersonas;

  const getPersonaLink = (personaId: number, key: string | number) => {
    const persona = allPersonas.find((p: PersonaBrief) => p.id === personaId);
    if (!persona) {
      return null;
    }
    return (
      <a
        target="_BLANK"
        rel="noreferrer"
        href={`/hosts/${personaId}`}
        key={key}
      >
        {persona.name}
      </a>
    );
  };

  return (
    <div className="app">
      <HeaderBar>
        <img
          src={musicIcon}
          alt="Audio Assets"
          height={32}
          style={{ display: "inline-block", marginLeft: 20, marginTop: 25 }}
        />
        <h1
          style={{
            display: "inline-block",
            marginLeft: 8,
            verticalAlign: "top",
            paddingTop: 13.5,
            fontWeight: 600,
          }}
        >
          Audio Assets
        </h1>
      </HeaderBar>
      <NavBar selectedIndex={5} />
      <div className="content" style={{ paddingLeft: 16, paddingRight: 16 }}>
        <h3>Upload New Audio Asset</h3>
        <div
          style={{
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <AudioPicker onValidAudioSelected={handleAudioSelected} />
          <LargeButton
            text={isUploading ? "Uploading..." : "Upload MP3"}
            onClick={handleUpload}
            disabled={isUploading || !selectedFile}
            loading={isUploading}
            icon={null}
            color="#34A853"
            style={{ marginLeft: "10px" }}
          />
        </div>
        {uploadMessage && (
          <p
            style={{
              marginTop: "10px",
              color:
                uploadMessage.startsWith("Upload failed") ||
                uploadMessage.startsWith("Error playing audio") ||
                uploadMessage.startsWith("User not")
                  ? "red"
                  : "green",
            }}
          >
            {uploadMessage}
          </p>
        )}

        <h3>Audio Asset Library</h3>
        {(isLoadingAssets || isLoadingPersonas) && <p>Loading resources...</p>}
        {combinedError && (
          <p style={{ color: "red" }}>
            Error loading resources: {combinedError.message}
          </p>
        )}
        {!isLoadingAssets &&
          !isLoadingPersonas &&
          !combinedError &&
          sortedAudioAssets.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  {/* <th>Type</th> */}
                  <th>Personas</th>
                  <th>Duration</th>
                  {/* <th>S3 Path</th> */}
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedAudioAssets.map((asset) => (
                  <tr key={asset.id}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {asset.name}
                      </div>
                    </td>
                    <td>{asset.description || "-"}</td>
                    {/* <td>{asset.type || '-'}</td> */}
                    <td>
                      <div className="persona-links">
                        {(asset.personas || [])
                          .filter((link) => link.isActive)
                          .map((link) => getPersonaLink(link.id, link.id)) ||
                          "-"}
                      </div>
                    </td>
                    <td>{formatDuration(asset.durationMs)}</td>
                    {/* <td>{asset.s3FilePath}</td> */}
                    <td>{asset.isActive ? "Yes" : "No"}</td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          gap: "5px",
                          flexWrap: "wrap",
                        }}
                      >
                        {asset.audioUrl && (
                          <SmallButton
                            text={
                              currentlyPlayingUrl === asset.audioUrl
                                ? "Pause"
                                : "Play"
                            }
                            onClick={() => handlePlayAudio(asset.audioUrl)}
                            style={{ minWidth: "60px" }}
                            isOutlineOnly={
                              currentlyPlayingUrl === asset.audioUrl
                            }
                            icon={null}
                          />
                        )}
                        <SmallButton
                          text="Edit"
                          icon={null}
                          isOutlineOnly={false}
                          onClick={() => handleEdit(asset)}
                        />
                        <SmallButton
                          text="Delete"
                          icon={null}
                          isOutlineOnly={true}
                          onClick={() => handleDelete(asset.id)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        {!isLoadingAssets &&
          !isLoadingPersonas &&
          !combinedError &&
          sortedAudioAssets.length === 0 && (
            <p>No audio assets found. Upload one to get started!</p>
          )}
      </div>

      {showEditModal && editingAsset && (
        <Modal isOpen={showEditModal} onClose={handleCloseModal}>
          <div className="modal-form-content-wrapper">
            <div className="checkbox-group">
              <input
                id={`asset-active-${editingAsset.id}`}
                type="checkbox"
                checked={modalIsActive}
                onChange={(e) => setModalIsActive(e.target.checked)}
              />
              <label htmlFor={`asset-active-${editingAsset.id}`}>Active</label>
            </div>
            <br />
            <label htmlFor={`asset-name-${editingAsset.id}`}>Name: </label>
            <input
              id={`asset-name-${editingAsset.id}`}
              type="text"
              value={modalName}
              onChange={(e) => setModalName(e.target.value)}
            />
            <br />
            <label htmlFor={`asset-desc-${editingAsset.id}`}>
              Description:
            </label>
            <textarea
              id={`asset-desc-${editingAsset.id}`}
              value={modalDescription}
              onChange={(e) => setModalDescription(e.target.value)}
            />
            <br />
            {/* <label htmlFor={`asset-type-${editingAsset.id}`}>Type (e.g., bgm, sfx):</label>
            <input id={`asset-type-${editingAsset.id}`} type="text" value={modalType} onChange={e => setModalType(e.target.value)} />
            <br /> */}
            <h4>Default Audio Settings</h4>
            <div className="form-row">
              <div className="form-field-vertical">
                <label htmlFor={`asset-vol-${editingAsset.id}`}>
                  Default Volume Override (0.0 - 1.0):
                </label>
                <input
                  id={`asset-vol-${editingAsset.id}`}
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={modalDefaultVolume}
                  onChange={(e) => setModalDefaultVolume(e.target.value)}
                  placeholder="0.5"
                />
              </div>
              <div className="form-field-vertical">
                <label htmlFor={`asset-fadecross-${editingAsset.id}`}>
                  Default Cross-Fade Override (ms):
                </label>
                <input
                  id={`asset-fadecross-${editingAsset.id}`}
                  type="number"
                  value={modalDefaultFadeCrossMs}
                  onChange={(e) => setModalDefaultFadeCrossMs(e.target.value)}
                  placeholder="3000"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field-vertical">
                <label htmlFor={`asset-fadein-${editingAsset.id}`}>
                  Default Fade-In Override (ms):
                </label>
                <input
                  id={`asset-fadein-${editingAsset.id}`}
                  type="number"
                  value={modalDefaultFadeInMs}
                  onChange={(e) => setModalDefaultFadeInMs(e.target.value)}
                  placeholder="3000"
                />
              </div>
              <div className="form-field-vertical">
                <label htmlFor={`asset-fadeout-${editingAsset.id}`}>
                  Default Fade-Out Override (ms):
                </label>
                <input
                  id={`asset-fadeout-${editingAsset.id}`}
                  type="number"
                  value={modalDefaultFadeOutMs}
                  onChange={(e) => setModalDefaultFadeOutMs(e.target.value)}
                  placeholder="3000"
                />
              </div>
            </div>

            <h4>Personas</h4>
            <div className="persona-links-scrollable-container">
              {isLoadingPersonas && <p>Loading personas for linking...</p>}
              {errorLoadingPersonas && (
                <p style={{ color: "red" }}>
                  Error loading personas: {errorLoadingPersonas.message}
                </p>
              )}
              {!isLoadingPersonas &&
                !errorLoadingPersonas &&
                modalPersonaLinks.map((link) => (
                  <div
                    key={link.personaId}
                    className="persona-link-edit"
                    style={{
                      border: "1px solid #eee",
                      padding: "10px",
                      marginBottom: "10px",
                    }}
                  >
                    <h5>
                      <input
                        id={`plink-active-${editingAsset.id}-${link.personaId}`}
                        type="checkbox"
                        checked={link.isActive}
                        onChange={(e) =>
                          handlePersonaLinkChange(
                            link.personaId,
                            "isActive",
                            e.target.checked
                          )
                        }
                      />
                      <label
                        htmlFor={`plink-active-${editingAsset.id}-${link.personaId}`}
                        style={{ marginLeft: "5px", userSelect: "none" }}
                      >
                        {link.personaName?.substring(0, 50) ||
                          `Persona ID: ${link.personaId}`}
                      </label>
                    </h5>

                    {link.isActive && (
                      <>
                        <div className="form-row" style={{ marginTop: "10px" }}>
                          <div className="form-field-vertical">
                            <label
                              htmlFor={`plink-vol-${editingAsset.id}-${link.personaId}`}
                            >
                              Volume Override (0.0-1.0):
                            </label>
                            <input
                              id={`plink-vol-${editingAsset.id}-${link.personaId}`}
                              type="number"
                              step="0.1"
                              min="0"
                              max="1"
                              value={
                                link.volumeOverride === null ||
                                link.volumeOverride === undefined
                                  ? ""
                                  : link.volumeOverride
                              }
                              onChange={(e) =>
                                handlePersonaLinkChange(
                                  link.personaId,
                                  "volumeOverride",
                                  e.target.value === ""
                                    ? undefined
                                    : parseFloat(e.target.value)
                                )
                              }
                              placeholder={
                                editingAsset?.defaultVolume !== null &&
                                editingAsset?.defaultVolume !== undefined
                                  ? String(editingAsset.defaultVolume)
                                  : "0.5"
                              }
                            />
                          </div>
                          <div className="form-field-vertical">
                            <label
                              htmlFor={`plink-fadecross-${editingAsset.id}-${link.personaId}`}
                            >
                              Cross-Fade Override (ms):
                            </label>
                            <input
                              id={`plink-fadecross-${editingAsset.id}-${link.personaId}`}
                              type="number"
                              value={
                                link.fadeCrossOverrideMs === null ||
                                link.fadeCrossOverrideMs === undefined
                                  ? ""
                                  : link.fadeCrossOverrideMs
                              }
                              onChange={(e) =>
                                handlePersonaLinkChange(
                                  link.personaId,
                                  "fadeCrossOverrideMs",
                                  e.target.value === ""
                                    ? undefined
                                    : parseInt(e.target.value, 10)
                                )
                              }
                              placeholder={
                                editingAsset?.defaultFadeCrossMs !== null &&
                                editingAsset?.defaultFadeCrossMs !== undefined
                                  ? String(editingAsset.defaultFadeCrossMs)
                                  : "3000"
                              }
                            />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-field-vertical">
                            <label
                              htmlFor={`plink-fadein-${editingAsset.id}-${link.personaId}`}
                            >
                              Fade-In Override (ms):
                            </label>
                            <input
                              id={`plink-fadein-${editingAsset.id}-${link.personaId}`}
                              type="number"
                              value={
                                link.fadeInOverrideMs === null ||
                                link.fadeInOverrideMs === undefined
                                  ? ""
                                  : link.fadeInOverrideMs
                              }
                              onChange={(e) =>
                                handlePersonaLinkChange(
                                  link.personaId,
                                  "fadeInOverrideMs",
                                  e.target.value === ""
                                    ? undefined
                                    : parseInt(e.target.value, 10)
                                )
                              }
                              placeholder={
                                editingAsset?.defaultFadeInMs !== null &&
                                editingAsset?.defaultFadeInMs !== undefined
                                  ? String(editingAsset.defaultFadeInMs)
                                  : "3000"
                              }
                            />
                          </div>
                          <div className="form-field-vertical">
                            <label
                              htmlFor={`plink-fadeout-${editingAsset.id}-${link.personaId}`}
                            >
                              Fade-Out Override (ms):
                            </label>
                            <input
                              id={`plink-fadeout-${editingAsset.id}-${link.personaId}`}
                              type="number"
                              value={
                                link.fadeOutOverrideMs === null ||
                                link.fadeOutOverrideMs === undefined
                                  ? ""
                                  : link.fadeOutOverrideMs
                              }
                              onChange={(e) =>
                                handlePersonaLinkChange(
                                  link.personaId,
                                  "fadeOutOverrideMs",
                                  e.target.value === ""
                                    ? undefined
                                    : parseInt(e.target.value, 10)
                                )
                              }
                              placeholder={
                                editingAsset?.defaultFadeOutMs !== null &&
                                editingAsset?.defaultFadeOutMs !== undefined
                                  ? String(editingAsset.defaultFadeOutMs)
                                  : "3000"
                              }
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
            </div>

            {saveError && <p className="error-message">{saveError}</p>}
            {isSaving && <p>Saving...</p>}
            <LargeButton
              text={isSaving ? "Saving..." : "Save Changes"}
              onClick={handleSaveModal}
              disabled={isSaving}
              loading={isSaving}
              icon={null}
              color="#34A853"
              style={{ marginTop: "16px" }}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AudioAssetsPage;
